import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, first, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Endpoints } from 'src/app/core/resources/endpoints';
import { Fmt } from 'src/app/core/messages/fmt';
import { SaasTransportCompany } from 'src/app/core/interfaces/saasTransportCompany';
import { AuthService } from './authentication.service';

/**
 * Dueño del listado de empresas de transporte de la SaaS.
 * Vive en core porque lo consumen tanto modules/cargo como modules/administration.
 */
@Injectable({ providedIn: 'root' })
export class SaasTransportCompanyService {

  private static readonly READY_POLL_MS = 250;
  private static readonly READY_TIMEOUT_MS = 10000;

  private cache: { [saasCompanyId: string]: Observable<SaasTransportCompany[]> } = {};

  constructor(
    private http: HttpClient,
    private endpoints: Endpoints,
    private authService: AuthService
  ) { }

  /**
   * true cuando la SaaS presta servicios de transporte de carga. Es la única
   * compuerta del bloque de empresas de transporte: una SaaS de escolta o de
   * candado satelital no tiene transportadoras que administrar, así que el
   * endpoint no aplica y no se consulta. El conteo de transportadoras no
   * interviene: con la compuerta abierta la regla aplica igual con una o con
   * cien.
   */
  get isEnabled(): boolean {
    return !!(this.authService && this.authService.hasTransportServicesCompany);
  }

  /**
   * Listado cacheado por empresa SaaS. Comparte una sola petición entre
   * todos los consumidores hasta que se invalide con clearCache().
   * Espera el contexto SaaS (whenReady) para que, al recargar la página, no
   * devuelva vacío solo porque la configuración aún no llegó.
   */
  list(): Observable<SaasTransportCompany[]> {
    return this.whenReady().pipe(switchMap(() => this.fetchList()));
  }

  private fetchList(): Observable<SaasTransportCompany[]> {
    if (!this.isEnabled) { return of([]); }
    const saasCompanyId = this.getSaasCompanyId();
    if (!saasCompanyId) { return of([]); }

    if (!this.cache[saasCompanyId]) {
      this.cache[saasCompanyId] = this.http.get<SaasTransportCompany[]>(
        `${environment.urlServerTeclogi}${Fmt.string(this.endpoints.saasTransportCompanies, saasCompanyId)}`
      ).pipe(
        map(companies => companies || []),
        // shareReplay también replica el error a los suscriptores tardíos, así
        // que un fallo dejaría el listado roto hasta el próximo clearCache().
        // Se descarta la entrada para que el siguiente list() vuelva a pedir.
        catchError(error => {
          delete this.cache[saasCompanyId];
          return throwError(error);
        }),
        shareReplay(1)
      );
    }
    return this.cache[saasCompanyId];
  }

  /**
   * Al recargar la página, AuthService carga la empresa SaaS y su
   * configuración por HTTP sin exponer una señal de "listo", y mientras tanto
   * isEnabled da false. Esto espera (sondeando) a que ambas estén disponibles:
   * emite true cuando lo están, o false si no llegan en READY_TIMEOUT_MS para
   * que el consumidor siga sin empresa de transporte en vez de quedarse colgado.
   */
  whenReady(): Observable<boolean> {
    if (this.isSaasContextReady) { return of(true); }
    const maxAttempts = Math.ceil(SaasTransportCompanyService.READY_TIMEOUT_MS / SaasTransportCompanyService.READY_POLL_MS);
    return timer(SaasTransportCompanyService.READY_POLL_MS, SaasTransportCompanyService.READY_POLL_MS).pipe(
      map(() => this.isSaasContextReady),
      first((ready, index) => ready || index + 1 >= maxAttempts)
    );
  }

  getById(companyId: string): Observable<SaasTransportCompany> {
    if (!companyId) { return of(null); }
    return this.list().pipe(
      map(companies => companies.find(company => company && company.companyId === companyId) || null)
    );
  }

  clearCache(): void {
    this.cache = {};
  }

  private get isSaasContextReady(): boolean {
    return !!this.getSaasCompanyId() && !!(this.authService && this.authService.getCompanySaaSConfig());
  }

  private getSaasCompanyId(): string {
    const saasCompany = this.authService && this.authService.getCompanySaaS();
    return saasCompany && saasCompany.companyId ? saasCompany.companyId : null;
  }
}
